import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ProductService } from '../services/product.service';
import * as ProductActions from './product.actions';
import {} from './product.selectors';

@Injectable()
export class ProductEffects {
    private _actions$ = inject(Actions);
    private logger = inject(LoggerService);
    private _productService = inject(ProductService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all products',
        conectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Products
    getAllProducts$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.loadProducts),
            switchMap(() => {
                return this._productService.getAll().pipe(
                    // Debug
                    // tap((res) => {
                    //     this.logger.log('response', res);
                    // }),
                    map((res) => {
                        return ProductActions.loadProductsSuccess({
                            products: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductActions.loadProductsFailure({
                                error: error.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All Products By Subcategory Id
    getProductsBySubcategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.loadProductsBySubcategory),
            switchMap((action) => {
                return this._productService
                    .getBySubcategoryId(action.subcategoryId)
                    .pipe(
                        // tap((res) => this.logger.log('response', res)), // Debug
                        map((res) => {
                            return ProductActions.loadProductsBySubcategorySuccess(
                                {
                                    products: res.data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                ProductActions.loadProductsBySubcategoryFailure(
                                    {
                                        error:
                                            error.error?.message ||
                                            'Unknown error',
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Get All Products By Subcategory Name
    getProductsBySubcategoryName$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.loadProductsBySubcategoryName),
            switchMap((action) => {
                return this._productService
                    .getBySubcategoryName(action.subcategoryName)
                    .pipe(
                        // tap((res) => this.logger.log('res', res)), // Debug
                        map((res) => {
                            return ProductActions.loadProductsBySubcategorySuccess(
                                {
                                    products: res.data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                ProductActions.loadProductsBySubcategoryFailure(
                                    {
                                        error:
                                            error.error?.message ||
                                            'Unknown error',
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Get All Products From Concession | Novelties Subcategories
    getAllProductsFromConcessionNoveltiesSubcategories$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.loadProductsFromConcessionNovelties),
            switchMap(() => {
                return this._productService
                    .getAllFromConcessionNovelties()
                    .pipe(
                        // Debug
                        tap((res) => {
                            this.logger.log('response', res);
                        }),
                        map((res) => {
                            if (!res) {
                                return ProductActions.loadProductFailure({
                                    error: 'No response received from server',
                                });
                            }
                            return ProductActions.loadProductsSuccess({
                                products: res.data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                ProductActions.loadProductsFailure({
                                    error:
                                        error.error?.message || 'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Product
    getProduct$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.loadProduct),
            switchMap((action) => {
                return this._productService.getOne(action.id).pipe(
                    // Debug
                    // tap((res) => {
                    //     this.logger.log('response', res);
                    // }),
                    map((res) => {
                        return ProductActions.loadProductSuccess({
                            product: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductActions.loadProductFailure({
                                error: error.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Product
    createProduct$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.createProduct),
            switchMap((action) => {
                return this._productService.create(action.productData).pipe(
                    // Debug
                    tap((res) => {
                        this.logger.log('response', res);
                    }),
                    map((response) => {
                        return ProductActions.createProductSuccess({
                            product: response,
                            success: 'Product created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductActions.createProductFailure({
                                error: error.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Product
    updateProduct$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.updateProduct),
            switchMap((action) => {
                return this._productService
                    .update(action.productData, action.id)
                    .pipe(
                        // Debug
                        // tap((response) => {
                        //     this.logger.log('response', response);
                        // }),
                        map((response) => {
                            return ProductActions.updateProductSuccess({
                                product: response,
                                success: 'Product updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                ProductActions.updateProductFailure({
                                    error:
                                        error.error?.message || 'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Product
    deleteProduct$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductActions.deleteProduct),
            switchMap((action) => {
                return this._productService.delete(action.id).pipe(
                    // Debug
                    // tap((response) => {
                    //     this.logger.log('response', response);
                    // }),
                    map(() => {
                        return ProductActions.deleteProductSuccess({
                            id: action.id,
                            success: 'Product deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductActions.deleteProductFailure({
                                error: error.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onProductActionSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                // ProductActions.loadProductsSuccess ||
                // ProductActions.loadProductSuccess ||
                ProductActions.createProductSuccess ||
                    ProductActions.updateProductSuccess ||
                    ProductActions.deleteProductSuccess
            ),
            tap((action) => {
                this._alertService.showAlert(action.success, 'success');
            })
        )
    );

    onProductActionFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                ProductActions.loadProductsFailure ||
                    ProductActions.loadProductFailure ||
                    ProductActions.createProductFailure ||
                    ProductActions.updateProductFailure ||
                    ProductActions.deleteProductFailure
            ),
            tap((action) => {
                this._alertService.showAlert(action.error, 'error');
            })
        )
    );
}
